const LIGHT_TEXT = "#f8fafc";
const DARK_TEXT = "#0f172a";

const clamp01 = (value) => Math.max(0, Math.min(1, value));

const channelToLinear = (channel) => {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
};

const rgbToLuminance = ({ r, g, b }) =>
  0.2126 * channelToLinear(r) +
  0.7152 * channelToLinear(g) +
  0.0722 * channelToLinear(b);

const parseHexColor = (value) => {
  const normalized = value.replace("#", "").trim();

  if (normalized.length === 3) {
    return {
      r: Number.parseInt(normalized[0] + normalized[0], 16),
      g: Number.parseInt(normalized[1] + normalized[1], 16),
      b: Number.parseInt(normalized[2] + normalized[2], 16),
    };
  }

  if (normalized.length === 6) {
    return {
      r: Number.parseInt(normalized.slice(0, 2), 16),
      g: Number.parseInt(normalized.slice(2, 4), 16),
      b: Number.parseInt(normalized.slice(4, 6), 16),
    };
  }

  return null;
};

const parseRgbColor = (value) => {
  const match = value.match(/rgba?\(([^)]+)\)/i);
  if (!match) {
    return null;
  }

  const [r, g, b] = match[1]
    .split(",")
    .slice(0, 3)
    .map((chunk) => Number.parseFloat(chunk.trim()));

  if ([r, g, b].some((channel) => Number.isNaN(channel))) {
    return null;
  }

  return { r, g, b };
};

const parseCssColorToRgb = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();

  if (trimmed.startsWith("#")) {
    return parseHexColor(trimmed);
  }

  if (trimmed.startsWith("rgb")) {
    return parseRgbColor(trimmed);
  }

  return null;
};

const contrastRatio = (firstLuminance, secondLuminance) => {
  const lightest = Math.max(firstLuminance, secondLuminance);
  const darkest = Math.min(firstLuminance, secondLuminance);
  return (lightest + 0.05) / (darkest + 0.05);
};

export const getCssColorLuminance = (value, fallback = 0.5) => {
  const rgb = parseCssColorToRgb(value);
  if (!rgb) {
    return fallback;
  }

  return rgbToLuminance(rgb);
};

export const analyzeImageLuminance = (imageUrl) =>
  new Promise((resolve, reject) => {
    if (!imageUrl || typeof window === "undefined") {
      reject(new Error("No image to analyze."));
      return;
    }

    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.decoding = "async";

    image.onload = () => {
      try {
        const sampleSize = 36;
        const canvas = document.createElement("canvas");
        canvas.width = sampleSize;
        canvas.height = sampleSize;

        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) {
          reject(new Error("Canvas context unavailable."));
          return;
        }

        context.drawImage(image, 0, 0, sampleSize, sampleSize);
        const { data } = context.getImageData(0, 0, sampleSize, sampleSize);

        let luminanceSum = 0;
        let alphaWeight = 0;

        for (let index = 0; index < data.length; index += 4) {
          const alpha = data[index + 3] / 255;
          if (alpha < 0.05) {
            continue;
          }

          luminanceSum +=
            rgbToLuminance({
              r: data[index],
              g: data[index + 1],
              b: data[index + 2],
            }) * alpha;
          alphaWeight += alpha;
        }

        resolve(alphaWeight ? clamp01(luminanceSum / alphaWeight) : 0.5);
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () => reject(new Error("Image load failed."));
    image.src = imageUrl;
  });

export const buildAdaptiveHeroPalette = ({
  backgroundLuminance,
  preferredTextColor,
}) => {
  const luminance = clamp01(backgroundLuminance ?? 0.5);
  const preferredLuminance = getCssColorLuminance(preferredTextColor, null);
  const darkLuminance = getCssColorLuminance(DARK_TEXT, 0.02);
  const lightLuminance = getCssColorLuminance(LIGHT_TEXT, 0.98);

  const preferredContrast =
    preferredLuminance === null
      ? 0
      : contrastRatio(luminance, preferredLuminance);
  const darkContrast = contrastRatio(luminance, darkLuminance);
  const lightContrast = contrastRatio(luminance, lightLuminance);

  let textColor = preferredTextColor || LIGHT_TEXT;
  if (preferredContrast < 4.5) {
    textColor = lightContrast >= darkContrast ? LIGHT_TEXT : DARK_TEXT;
  }

  const usesDarkText = textColor !== LIGHT_TEXT;

  return {
    backgroundLuminance: luminance,
    usesDarkText,
    textColor,
    mutedTextColor: usesDarkText
      ? "rgba(15,23,42,0.84)"
      : "rgba(248,250,252,0.94)",
    overlayBackground: usesDarkText
      ? "linear-gradient(180deg, rgba(255,255,255,0.01) 0%, rgba(255,255,255,0) 44%, rgba(255,255,255,0.08) 100%)"
      : "linear-gradient(180deg, rgba(15,23,42,0.02) 0%, rgba(15,23,42,0.12) 68%, rgba(15,23,42,0.26) 100%)",
    titleShadow: usesDarkText
      ? "0 1px 0 rgba(255,255,255,0.48), 0 10px 28px rgba(255,255,255,0.16)"
      : "0 1px 0 rgba(15,23,42,0.14), 0 12px 28px rgba(15,23,42,0.28)",
    textShadow: usesDarkText
      ? "0 1px 0 rgba(255,255,255,0.42), 0 8px 20px rgba(255,255,255,0.12)"
      : "0 1px 0 rgba(15,23,42,0.14), 0 10px 22px rgba(15,23,42,0.24)",
    badgeBackground: usesDarkText
      ? "rgba(255,255,255,0.78)"
      : "rgba(15,23,42,0.24)",
    badgeBorder: usesDarkText
      ? "rgba(15,23,42,0.16)"
      : "rgba(255,255,255,0.18)",
    badgeTextColor: usesDarkText ? DARK_TEXT : LIGHT_TEXT,
    badgeShadow: usesDarkText
      ? "0 12px 28px rgba(255,255,255,0.12)"
      : "0 12px 28px rgba(15,23,42,0.22)",
    secondaryBadgeBackground: usesDarkText
      ? "rgba(255,255,255,0.62)"
      : "rgba(15,23,42,0.18)",
    secondaryBadgeBorder: usesDarkText
      ? "rgba(15,23,42,0.12)"
      : "rgba(255,255,255,0.14)",
  };
};

export const DEFAULT_LIGHT_TEXT = LIGHT_TEXT;
export const DEFAULT_DARK_TEXT = DARK_TEXT;
